<template>
  <span>{{ displayValue }}</span>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue'

export default {
  name: 'AnimatedNumber',
  props: {
    value: {
      type: [Number, String],
      required: true,
      default: 0,
      validator: (value) => {

        return !isNaN(Number(value))
      }
    },
    duration: {
      type: Number,
      default: 1000
    },
    formatValue: {
      type: Function,
      default: (value) => value.toString()
    },
    easing: {
      type: String,
      default: 'easeOutCubic'
    }
  },
  setup(props) {
    const currentValue = ref(0)
    const startTime = ref(null)
    const startValue = ref(0)
    const isAnimating = ref(false)

    const numericValue = computed(() => {
      const num = Number(props.value)
      return isNaN(num) ? 0 : num
    })

    const displayValue = computed(() => {
      return props.formatValue(currentValue.value)
    })

    const easingFunctions = {
      linear: (t) => t,
      easeOutCubic: (t) => 1 - Math.pow(1 - t, 3),
      easeInOutCubic: (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,
      easeOutQuad: (t) => 1 - (1 - t) * (1 - t),
      easeInOutQuad: (t) => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2
    }

    const animate = (timestamp) => {
      if (!startTime.value) startTime.value = timestamp
      
      const elapsed = timestamp - startTime.value
      const progress = Math.min(elapsed / props.duration, 1)
      
      const easingFunc = easingFunctions[props.easing] || easingFunctions.easeOutCubic
      const easedProgress = easingFunc(progress)
      
      currentValue.value = startValue.value + (numericValue.value - startValue.value) * easedProgress
      
      if (progress < 1) {
        requestAnimationFrame(animate)
      } else {
        isAnimating.value = false
        currentValue.value = numericValue.value
      }
    }

    const startAnimation = () => {
      if (isAnimating.value) return
      
      startValue.value = currentValue.value
      startTime.value = null
      isAnimating.value = true
      requestAnimationFrame(animate)
    }

    watch(() => numericValue.value, (newValue, oldValue) => {
      if (newValue !== oldValue) {
        startAnimation()
      }
    })

    onMounted(() => {
      currentValue.value = numericValue.value
    })

    return {
      displayValue
    }
  }
}
</script>